import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

export const useOutsideClickRef = <T extends HTMLElement>(
  onClickOutside: () => void,
): RefObject<T> => {
  const ref = useRef<T>(null)

  const handleClick = (e: MouseEvent) => {
    if (ref?.current?.contains(e.target as Node)) {
      return
    }

    onClickOutside()
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [])

  return ref
}

export const useNotifyingState = <T>(
  initialValue: T | (() => T),
  notifiers: ((value: T) => void)[],
): [T, Dispatch<SetStateAction<T>>] => {
  const [state, setState] = useState(initialValue)

  const setStateAndNotify = useCallback(
    (action: SetStateAction<T>) => {
      setState((prev) => {
        const nextValue =
          typeof action === 'function' ? (action as (prevState: T) => T)(prev) : action
        notifiers.forEach((notifier) => notifier(nextValue))
        return nextValue
      })
    },
    [...notifiers],
  )

  return [state, setStateAndNotify]
}
